import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useMemo } from "react";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 440,
  bgcolor: "#f7f7f7",
  borderRadius: "16px",
  boxShadow: "0 10px 40px rgba(0,0,0,0.14)",
  p: "24px",
  outline: "none",
};

const pill = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  px: 1.25,
  py: 0.75,
  borderRadius: "999px",
  bgcolor: "rgba(1,36,66,0.06)", // #012442 bem leve
  color: "#012442",
  fontWeight: 600,
  fontSize: 13,
};

const infoCard = {
  borderRadius: "14px",
  bgcolor: "#ffffff",
  border: "1px solid rgba(1,36,66,0.12)",
  p: 2,
};

const row = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  py: 1,
};

const label = {
  color: "rgba(1,36,66,0.72)",
  fontSize: 13,
  fontWeight: 600,
};

const value = {
  color: "#012442",
  fontSize: 14,
  fontWeight: 600,
  textAlign: "right",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export default function FreightEditedSuccessModal({
  open,
  handleClose,
  onResult,
  dataMsg,
  title = "Tudo certo!",
}) {
  const data = useMemo(() => {
    const freightNumber = dataMsg?.freightNumber ?? "—";
    const msg = dataMsg?.msg ?? "Operação concluída com sucesso.";
    const origem = dataMsg?.firstDelivery?.name ?? "—";
    const origemUF = dataMsg?.firstDelivery?.uf ?? "—";
    const destino = dataMsg?.lastDelivery?.name ?? "—";
    const destinoUF = dataMsg?.lastDelivery?.uf ?? "—";
    return { freightNumber, msg, origem, origemUF, destino, destinoUF };
  }, [dataMsg]);

  const handleConfirmClick = () => {
    onResult?.(true);
    // se você quiser fechar aqui, descomenta:
    // handleClose?.();
  };

  return (
    <Modal
      keepMounted
      open={open}
      onClose={handleClose}
      aria-labelledby="freight-edited-title"
      aria-describedby="freight-edited-description"
    >
      <Box sx={style}>
        {/* HEADER */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            onClick={handleClose}
            sx={{
              p: 0,
              width: 40,
              height: 40,
              borderRadius: "999px",
              "&:hover": { bgcolor: "rgba(1,36,66,0.06)" },
            }}
            aria-label="Fechar"
          >
            <CloseIcon sx={{ color: "#012442" }} />
          </IconButton>

          <Typography
            id="freight-edited-title"
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#012442",
              flexGrow: 1,
              textAlign: "center",
              mr: "40px",
              letterSpacing: "-0.2px",
            }}
          >
            {title}
          </Typography>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* BODY */}
        <Stack spacing={2}>
          {data.isSave ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleRoundedIcon sx={{ color: "#012442" }} />
              <Box sx={pill}>Frete Atualizado</Box>
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleRoundedIcon sx={{ color: "#012442" }} />
              <Box sx={pill}>Frete Salvo</Box>
            </Stack>
          )}

          <Typography
            id="freight-edited-description"
            sx={{
              color: "#012442",
              fontSize: 18,
              lineHeight: 1.5,
              fontWeight: 700,
            }}
          >
            {data.msg}
          </Typography>

          <Box sx={infoCard}>
            <Box sx={row}>
              <Typography sx={label}>🚚 Frete</Typography>
              <Typography sx={value} title={String(data.freightNumber)}>
                {data.freightNumber}
              </Typography>
            </Box>

            <Divider sx={{ borderColor: "rgba(1,36,66,0.10)" }} />

            <Box sx={row}>
              <Typography sx={label}>⛟ Origem</Typography>
              <Typography sx={value} title={`${data.origem} - ${data.origemUF}`}>
                {data.origem} - {data.origemUF}
              </Typography>
            </Box>

            <Divider sx={{ borderColor: "rgba(1,36,66,0.10)" }} />

            <Box sx={row}>
              <Typography sx={label}>🏁 Destino</Typography>
              <Typography sx={value} title={`${data.destino} - ${data.destinoUF}`}>
                {data.destino} - {data.destinoUF}
              </Typography>
            </Box>
          </Box>
        </Stack>

        {/* FOOTER */}
        <Stack spacing={1.5} sx={{ mt: 3 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleConfirmClick}
            sx={{
              backgroundColor: "#ff0000",
              borderRadius: "12px",
              py: 1.35,
              fontWeight: 700,
              textTransform: "none",
              fontSize: "15px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#cc0000",
                boxShadow: "none",
              },
            }}
          >
            Ok, entendi
          </Button>

          {/* opcional: ação secundária Airbnb */}
          {/* <Button
            onClick={handleClose}
            sx={{
              color: "#012442",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              textDecoration: "underline",
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            Fechar
          </Button> */}
        </Stack>
      </Box>
    </Modal>
  );
}
